import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { sizeOptions, seasonOptions } from "utils/data";
import Slider from "@mui/material/Slider";
import parse from "html-react-parser";

const Color = require("color");

const PostStyleModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      post,
      setPost,
      handleRefresh = () => {},
      handleAfterOk = () => {},
    },
    ref
  ) => {
    const [newColor, setNewColor] = useState("#ffffff");

    if (ref) {
      useImperativeHandle(
        ref,
        () => ({
          newColor,
        }),
        [setNewColor]
      );
    }

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    useEffect(() => {
      reset({
        space: post?.metadata?.settings?.mb,
      });
      setNewColor(post?.metadata?.settings?.backgroundColor);
    }, []);

    const handleUpdateLayout = async (data) => {
      const newPost = { ...post };
      if (newPost?.metadata?.settings?.backgroundColor) {
        newPost.metadata.settings.backgroundColor = newColor;
      }
      if (newPost?.metadata?.settings) {
        newPost.metadata.settings.mb = data.space;
      }
      setPost(newPost);
      handleClose();
    };

    return (
      <Dialog
        open={visible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
        fullWidth={true}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {"Post Layout"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleOk ? handleOk : handleUpdateLayout)}
          style={{ overflow: "auto", maxHeight: "600px" }}
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <SuiBox sx={{ px: 2 }}>
                <SuiBox display="flex" justifyContent="center" alignItems="center" mb={2}>
                  <SuiBox mb={1} ml={0.5} lineHeight={0} sx={{ minWidth: 120 }}>
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Background Color
                    </SuiTypography>
                  </SuiBox>
                  <input
                    type="color"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    style={{
                      cursor: "pointer",
                      marginRight: "4px",
                      height: 36,
                      width: 100,
                    }}
                  />
                  <SuiTypography variant="body2" color="text" sx={{ mx: 1 }}>
                    Or
                  </SuiTypography>

                  <Controller
                    name="hex"
                    control={control}
                    render={({ field }) => (
                      <SuiInput
                        {...field}
                        value={newColor}
                        onChange={(e) => {
                          let value = e.target.value.toUpperCase();
                          value = value.replace(/[^\dA-F]/g, "");
                          if (!value.startsWith("#")) {
                            value = "#" + value;
                          }

                          setNewColor(value);
                        }}
                        inputProps={{ maxLength: 7 }}
                      />
                    )}
                  />
                </SuiBox>
                <SuiBox display="flex" justifyContent="center">
                  <SuiBox mb={1} ml={0.5} lineHeight={0} sx={{ minWidth: 120 }}>
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Content Spacing
                    </SuiTypography>
                  </SuiBox>

                  <Controller
                    name="space"
                    control={control}
                    render={({ field }) => (
                      <Slider {...field} valueLabelDisplay="auto" min={0} max={150} />
                    )}
                  />
                </SuiBox>
              </SuiBox>
              <SuiBox
                pt={3}
                px={3}
                display={"flex"}
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="text" size="medium" onClick={handleClose} color="dark">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                      Save
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </form>
      </Dialog>
    );
  }
);

export default PostStyleModal;
